import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { PermissionAxios } from "../../axios/permission/permission.axios";
import { PermissionInvite } from "../../model/permissions/PermisisonInvite";
import { GetPermissionInviteByIdDto } from "../../model/permissions/dto/GetPermissionInviteByIdDto";
import { PermissionCancelInviteDto } from "../../model/permissions/dto/PermissionCancelInviteDto";
import { PermissionLeaveOrganizationDto } from "../../model/permissions/dto/PermissionLeaveOrganizationDto";
import { PermissionRemoveMemberByIdDto } from "../../model/permissions/dto/PermissionRemoveMemberByIdDto";
import { PermissionRespondToInviteDto } from "../../model/permissions/dto/PermissionRespondToInviteDto";
import { PermissionUpdateMemberPermissionsDto } from "../../model/permissions/dto/PermissionUpdateMemberPermissionsDto";
import { PermissionUpdateMemberRoleDto } from "../../model/permissions/dto/PermissionUpdateMemberRoleDto";
import { PermissionsInviteMembersDto } from "../../model/permissions/dto/PermissionsInviteMembersDto";
import { GenericWithUserId } from "../../model/shared/dto/GenericWithUserId";
import { User } from "../../model/user/User";
import { LoginResponseDto } from "../../model/user/dto/LoginResponseDto";
import { Login2faRequiredDto } from "../../model/user/dto/Login2faRequiredDto";
import { SignupSuccessDto } from "../../model/user/dto/SignupSuccessDto";
import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "./../user/user.slice";
import { revertAllReduxAction } from "../store-reset";

interface PermissionReducer {
  inviteLoading?: boolean;
  invitedMembers?: PermissionInvite[];
  invitedMembersLoading?: boolean;
  //
  currentInvite: PermissionInvite | null;
  members: User[];
}

const initialState = Object.freeze<PermissionReducer>({
  inviteLoading: false,
  invitedMembers: [],
  invitedMembersLoading: false,
  currentInvite: null,
  members: [],
});

const inviteMembers = AtiraThunk<void, PermissionsInviteMembersDto>(
  `/permissions/invite-members`,
  (dto) => PermissionAxios.inviteMembers(dto),
);

const getInvitedMembers = AtiraThunk<PermissionInvite[], GenericWithUserId>(
  "/permissions/invited-members",
  (dto) => PermissionAxios.getInvitedMembers(dto),
);

const getInviteByCode = AtiraThunk<
  PermissionInvite,
  GetPermissionInviteByIdDto
>("/permissions/invite/:id", PermissionAxios.getInviteByCode);

const respondToInvite = AtiraThunk<void, PermissionRespondToInviteDto>(
  "permissions/respond",
  (dto) => PermissionAxios.respondToInvite(dto),
);

const getMembers = AtiraThunk<User[], GenericWithUserId>(
  "permissions/members",
  PermissionAxios.getMembers,
);

const removeMember = AtiraThunk<void, PermissionRemoveMemberByIdDto>(
  "permissions/member/:id/remove",
  (dto) => PermissionAxios.removeMember(dto),
);

const leaveOrganization = AtiraThunk<void, PermissionLeaveOrganizationDto>(
  "/permission/member/leave",
  (dto) => PermissionAxios.leaveOrganization(dto),
);

const updateMemberPermissions = AtiraThunk<
  void,
  PermissionUpdateMemberPermissionsDto
>("/permissions/member/:id/update-permissions", (dto) =>
  PermissionAxios.updateMemberPermissions(dto),
);

const updateMemberRole = AtiraThunk<void, PermissionUpdateMemberRoleDto>(
  "/permissions/member/:id/update-role",
  (dto) => PermissionAxios.updateMemberRole(dto),
);

const cancelPermissionInvite = AtiraThunk<void, PermissionCancelInviteDto>(
  "/permissions/invite/:id/cancel",
  (dto) => PermissionAxios.cancelPermissionInvite(dto),
);

const permissionSlice = createSlice({
  name: "permission",
  initialState,
  reducers: {
    resetCurrentInvite: (state, action) => {
      state.currentInvite = null;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(inviteMembers.pending, (state, action) => {
      state.inviteLoading = true;
    });
    addCase(inviteMembers.fulfilled, (state, action) => {
      state.inviteLoading = false;
    });
    addCase(inviteMembers.rejected, (state, action) => {
      state.inviteLoading = false;
    });
    //
    addCase(getInvitedMembers.pending, (state, action) => {
      state.invitedMembersLoading = true;
    });
    addCase(getInvitedMembers.fulfilled, (state, action) => {
      state.invitedMembers = action.payload;
      state.invitedMembersLoading = false;
    });
    addCase(getInvitedMembers.rejected, (state, action) => {
      state.invitedMembersLoading = false;
    });
    //
    addCase(getInviteByCode.pending, (state, action) => {
      state.inviteLoading = true;
    });
    addCase(getInviteByCode.fulfilled, (state, action) => {
      state.currentInvite = action.payload;
      state.inviteLoading = false;
    });
    addCase(getInviteByCode.rejected, (state, action) => {
      state.inviteLoading = false;
    });
    addCase(userActions.getLoggedInUser.fulfilled, (state, action) => {
      if (action.payload.inviteInfo) {
        state.currentInvite = action.payload.inviteInfo;
      }
    });
    //

    addCase(getMembers.fulfilled, (state, action) => {
      state.members = action.payload;
    });

    addMatcher(
      isAnyOf(
        userActions.loginWithGoogle.fulfilled,
        userActions.verifyUserAccount.fulfilled,
        userActions.login.fulfilled,
        userActions.verifyLogin2fa.fulfilled,
      ),
      (
        state,
        action: PayloadAction<
          SignupSuccessDto | LoginResponseDto | Login2faRequiredDto
        >,
      ) => {
        // A 2FA-required login carries no invite info; it arrives with the
        // later verifyLogin2fa.fulfilled instead.
        if (
          "fromInvite" in action.payload &&
          action.payload.fromInvite &&
          action.payload.invite
        ) {
          state.currentInvite = action.payload.invite;
        }
      },
    );

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const permissionActions = {
  ...permissionSlice.actions,
  inviteMembers,
  getInvitedMembers,
  getInviteByCode,
  respondToInvite,
  getMembers,
  removeMember,
  leaveOrganization,
  updateMemberPermissions,
  updateMemberRole,
  cancelPermissionInvite,
};

export default permissionSlice.reducer;
